import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import {
  CalendarDays, CheckCircle2, PenLine, Sparkles, TrendingUp, Video,
} from 'lucide-react';

const topics = ['Quadratics', 'Circle theorems', 'Vectors'];

const progress = [
  { label: 'Algebra', value: 92 },
  { label: 'Geometry', value: 78 },
  { label: 'Statistics', value: 64 },
];

/**
 * A composed, code-drawn preview of the product used as the hero visual.
 * Deliberately not a screenshot so it stays crisp and themeable.
 */
const HeroPreview: React.FC = () => {
  const reduceMotion = useReducedMotion();
  const floatClass = reduceMotion ? '' : 'animate-float';

  return (
    <motion.div
      className="relative mx-auto w-full max-w-[26rem] sm:max-w-[34rem] sm:pl-24 sm:pr-14"
      initial={reduceMotion ? false : { opacity: 0, y: 28, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.9, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
    >
      {/* Ambient glow behind the card stack */}
      <div
        aria-hidden="true"
        className="absolute inset-6 -z-10 rounded-full bg-brand-500/20 blur-3xl dark:bg-brand-500/25"
      />

      {/* Main lesson card */}
      <div className="surface shadow-lift overflow-hidden bg-base-100/90 backdrop-blur">
        <div className="flex items-center gap-2 border-b border-ink-900/[0.07] px-5 py-3 dark:border-white/10">
          <span className="h-2.5 w-2.5 rounded-full bg-ink-900/15 dark:bg-white/20" />
          <span className="h-2.5 w-2.5 rounded-full bg-ink-900/15 dark:bg-white/20" />
          <span className="h-2.5 w-2.5 rounded-full bg-ink-900/15 dark:bg-white/20" />
          <p className="ml-2 text-xs font-medium text-base-content/50">Live lesson</p>
          <span className="ml-auto flex items-center gap-1.5 text-xs font-semibold text-emerald-600 dark:text-emerald-400">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-500/70" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
            </span>
            On now
          </span>
        </div>

        <div className="p-5">
          <div className="flex items-center gap-3.5">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-brand-500 to-brand-700 text-base font-bold text-white">
              AM
            </div>
            <div className="min-w-0">
              <p className="truncate text-[15px] font-semibold">Amara M.</p>
              <p className="truncate text-xs text-base-content/60">
                GCSE Maths &middot; Imperial College
              </p>
            </div>
            <div className="ml-auto flex gap-1.5">
              <span className="grid h-9 w-9 place-items-center rounded-lg bg-brand-600 text-white">
                <Video className="h-4 w-4" />
              </span>
              <span className="grid h-9 w-9 place-items-center rounded-lg bg-base-200 text-base-content/70">
                <PenLine className="h-4 w-4" />
              </span>
            </div>
          </div>

          <div className="mt-5 flex flex-wrap gap-1.5">
            {topics.map((topic) => (
              <span
                key={topic}
                className="rounded-full border border-ink-900/[0.08] bg-base-200/70 px-2.5 py-1 text-xs font-medium text-base-content/70 dark:border-white/10"
              >
                {topic}
              </span>
            ))}
          </div>

          <div className="mt-5 space-y-3">
            {progress.map((row) => (
              <div key={row.label}>
                <div className="mb-1.5 flex justify-between text-xs font-medium">
                  <span className="text-base-content/70">{row.label}</span>
                  <span className="tabular-nums text-base-content/50">
                    {row.value}
                    %
                  </span>
                </div>
                <div className="h-1.5 overflow-hidden rounded-full bg-base-300">
                  <motion.div
                    className="h-full rounded-full bg-gradient-to-r from-brand-500 to-brand-400"
                    initial={reduceMotion ? false : { width: 0 }}
                    animate={{ width: `${row.value}%` }}
                    transition={{ duration: 1.1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  />
                </div>
              </div>
            ))}
          </div>

          <div className="mt-5 flex items-center gap-2 rounded-xl border border-brand-500/20 bg-brand-500/[0.07] px-3.5 py-3">
            <Sparkles className="h-4 w-4 shrink-0 text-brand-600 dark:text-brand-400" />
            <p className="text-xs leading-snug text-base-content/75">
              Recap generated automatically — 3 practice questions sent to your inbox.
            </p>
          </div>
        </div>
      </div>

      {/* Floating stat chips */}
      <div
        className={`absolute left-0 top-32 hidden sm:block ${floatClass}`}
        style={{ animationDelay: '-2s' }}
      >
        <div className="surface shadow-lift flex items-center gap-2.5 bg-base-100/95 px-3.5 py-2.5 backdrop-blur">
          <TrendingUp className="h-4 w-4 text-emerald-500" />
          <div>
            <p className="text-xs font-semibold leading-tight">+2 grades</p>
            <p className="text-[11px] leading-tight text-base-content/55">average uplift</p>
          </div>
        </div>
      </div>

      <div className={`absolute -bottom-5 right-0 hidden sm:block ${floatClass}`}>
        <div className="surface shadow-lift flex items-center gap-2.5 bg-base-100/95 px-3.5 py-2.5 backdrop-blur">
          <CalendarDays className="h-4 w-4 text-brand-600 dark:text-brand-400" />
          <div>
            <p className="text-xs font-semibold leading-tight">Thu, 4:30pm</p>
            <p className="text-[11px] leading-tight text-base-content/55">next session booked</p>
          </div>
        </div>
      </div>

      <div
        className={`absolute -top-4 right-2 hidden sm:block ${floatClass}`}
        style={{ animationDelay: '-4s' }}
      >
        <div className="surface shadow-lift flex items-center gap-2 bg-base-100/95 px-3 py-2 backdrop-blur">
          <CheckCircle2 className="h-4 w-4 text-brand-600 dark:text-brand-400" />
          <p className="text-xs font-semibold">DBS verified</p>
        </div>
      </div>
    </motion.div>
  );
};

export default HeroPreview;
